import { useState, type CSSProperties, type Ref, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { MdBrokenImage } from 'react-icons/md';
import './MediaThumbnail.scss';

interface MediaThumbnailProps {
  /** Resolved URL (picUrl / mediaThumbUrl output); '' / null / undefined = none. */
  src: string | null | undefined;
  alt: string;
  /** pic-service processing_state. Only 'pending' shows "processing"; anything else without an image is broken. */
  state?: 'pending' | 'ready' | 'failed';
  /** Applied to the <img> AND the fallback box, so the fallback fills the same slot. */
  className?: string;
  style?: CSSProperties;
  /** On whichever element renders (the <img> or the fallback box), e.g. to observe it. */
  ref?: Ref<HTMLElement>;
  /** Fires when the image has loaded (not for the fallbacks). */
  onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
}

/**
 * The one way to render a media thumbnail / display image. pic-service reports
 * facts — processing_state, and a null URL whenever it has no rendition of the
 * live master — and this decides what the user sees: the image, a pulsing
 * "processing" box, or a broken-image icon. Only the client can also see an
 * image that fails to LOAD (expired presign, object gone, undecodable bytes),
 * so that case lands on the broken icon too.
 */
const MediaThumbnail = ({ src, alt, state, className, style, onLoad, ref }: MediaThumbnailProps) => {
  const { t } = useTranslation();
  // Remember WHICH src failed, so a new src (refetch, fresh presign) gets a fresh try.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const classes = (extra: string) => ['media-thumbnail-fallback', extra, className].filter(Boolean).join(' ');

  if (src && src !== failedSrc) {
    return (
      <img
        ref={ref as Ref<HTMLImageElement>}
        src={src}
        alt={alt}
        className={className}
        style={style}
        loading="lazy"
        decoding="async"
        onLoad={onLoad}
        onError={() => setFailedSrc(src)}
      />
    );
  }

  if (state === 'pending') {
    return <span ref={ref} className={classes('is-processing')} style={style} aria-label={t('album.processing')} />;
  }

  return (
    <span ref={ref} className={classes('is-broken')} style={style} role="img" aria-label={t('media.unavailable')}>
      <MdBrokenImage aria-hidden />
    </span>
  );
};

export default MediaThumbnail;
